import { Box, LoadingOverlay, Paper, Stack, Text, Title } from "@mantine/core";

import { IconBrain } from "@tabler/icons-react";
import { PatientHeader } from "./components/PatientHeader";
import { Printable } from "@components";
import { SimplifiedOcularMotilityCard } from "./components/SimplifiedOcularMotilityCard";
import { SimplifiedPupillometryCard } from "./components/SimplifiedPupillometryCard";
import { getOverallStatus } from "./utils/neurologicalCalculations";
import useNeurologicalData from "./hooks/useNeurologicalData";

export interface NeurologicalAssessmentProps {
  hidePrintButton?: boolean;
  pupillometryUuid?: string;
  ocularMotilityUuid?: string;
}

export default function NeurologicalAssessment({
  pupillometryUuid,
  ocularMotilityUuid,
}: NeurologicalAssessmentProps = {}) {
  const { patientData, pupillometryData, ocularMotilityData, loading } = useNeurologicalData({
    pupillometryUuid,
    ocularMotilityUuid,
  });

  const overallStatus = getOverallStatus(pupillometryData, ocularMotilityData);

  return (
    <Printable.Section>
      <Box>
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

        <PatientHeader
          patientData={patientData}
          icon={<IconBrain color="white" size={24} />}
          title="Neurological Assessment"
          subtitle="Neuro-Ophthalmological Evaluation"
          overallStatus={{
            label: `Overall: ${overallStatus.status}`,
            color: overallStatus.color,
          }}
        />

        <Stack gap="lg">
          <SimplifiedPupillometryCard data={pupillometryData} />
          <SimplifiedOcularMotilityCard data={ocularMotilityData} />
        </Stack>

        <Paper
          shadow="md"
          p="xl"
          radius="md"
          mt="md"
          withBorder
          style={{
            backgroundColor: "var(--oculera-tint-orange)",
            borderColor: "var(--oculera-tint-orange-border)",
          }}
        >
          <Title order={4} mb="sm" c="orange.8">
            Neurological Summary
          </Title>
          <Text size="sm" c="orange.9">
            {overallStatus.status === "Normal"
              ? `${patientData.name}'s pupil reactions and eye tracking stayed within the expected range on this screening. No clear high-risk pattern is flagged here, but these findings should still be interpreted alongside the full clinical picture.`
              : `${patientData.name}'s neurological screening shows at least one area that may warrant closer review. The summary cards above highlight the first findings a clinician would usually check and can help guide whether clinical follow-up is appropriate.`}
          </Text>
        </Paper>

        <Text size="xs" c="dimmed" ta="center" mt="md" mb="md">
          This report is for informational purposes. Please consult with your neurologist or
          ophthalmologist for medical advice.
        </Text>
      </Box>
    </Printable.Section>
  );
}
